<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';

import type {
  BoostOffer,
  DiamondPack,
  FuelRefillKey,
  FuelRefillTier,
  PremiumShop,
  TankBundle,
  Wallet,
} from '@/net/api';
import { askToBuy } from '@/state/confirm-purchase';
import { boostRemaining, formatPrice } from './format';
import ItemPreview from './ItemPreview.vue';

/**
 * The store.
 *
 * One screen for everything diamonds touch, shaped like a storefront rather
 * than a document: a category rail down the left — Featured, Diamonds, Tanks,
 * Boosts — and a grid of full-art cards, each with the price on a plate along
 * its bottom edge. The plate *is* the buy button, so what a card costs and
 * where you click to pay are the same pixels.
 *
 * `Supplies` is gone as a category. It used to hold three unrelated things —
 * the fuel refill, converting diamonds to credits, and a footnote about
 * top-tier upgrades — and none of the three is a *shelf of goods* in the way
 * a pack, a boost or a bundle is. Fuel moved onto the Boosts pane, first and
 * largest: it is bought over and over, exactly like a boost is, and it earns
 * the attention a fixed, generous card gives it. Converting moved further
 * still, off the Shop tab entirely and onto the credits readout in the title
 * bar — see `ConvertCreditsModal.vue` for why a currency exchange is not a
 * product on a shelf. The top-tier footnote is gone from the store outright:
 * it explained a control that lives on the Hulls and Turrets tabs, which is
 * where a player actually meets it — the customise ladder itself is the
 * explanation, and a shelf entry that sells nothing was noise on a pane that
 * now leads with fuel.
 *
 * Two kinds of picture, and the split is deliberate. A bundle's art is the
 * real tank — `ItemPreview` with `framing="tank"`, the same cached still every
 * catalogue card uses — because the shop shows the thing, and a painting of a
 * tank could drift from the tank. Everything else on sale has no mesh to
 * photograph (a pile of diamonds, a day of double XP, a tank of fuel), so
 * those cards carry authored art from `/assets/store/`, derived from the offer
 * id the same way part and map art derive from theirs. A missing image
 * degrades to the card's own dark plate with the label still on it — an honest
 * placeholder, never a broken-image glyph.
 *
 * Featured is a curation, not a category: the live limited offers, the
 * highlighted pack, and the weekly boosts. Full-price bundles are
 * deliberately NOT on it any more — only a bundle under a live offer earns
 * the landing pane, and the Tanks pane is the one place the whole roster
 * lives. (Featured used to carry every bundle so the e2e harness and a
 * player following a link could count on one being buyable without knowing
 * the rail exists; the Tanks pane owns that job now, and `shop-bundles.mjs`
 * clicks the rail.) Featured is also the one pane that reads as *featured*:
 * its cards render at hero scale — larger cards, a richer glow, a small
 * "Featured" flag — while every other pane keeps the ordinary card size,
 * because there the cards are a catalogue to compare rather than a handful
 * of things being pointed at.
 *
 * Limited offers lead Featured, in one mixed row — every discounted bundle
 * and every discounted pack together under a single "Limited offers" heading,
 * because two stacked one-item shelves read as two afterthoughts where one
 * row reads as a sale. An offer is a discount on something already on sale —
 * the served price is already the discounted one, the view carries the
 * standing figure and the deadline, and the countdown is a subtraction against
 * the tab's own ticking clock. A bundle under a live offer stays on the Tanks
 * pane's full list as well (the old rule that pulled it off the regular shelf
 * is deprecated — the card carries its struck standing price, deal flag and
 * discounted plate wherever it renders, so there is no wrong place to click,
 * only a second place to find the same deal). When the
 * clock runs out the card falls back to the standing price and stops posting
 * the offer id, so a stale tab can never quote a figure the server would then
 * exceed — the server refuses a pinned offer that has ended rather than
 * charging the full price the player never saw.
 *
 * Every price here came from the server in this response — nothing is computed
 * locally, and a disabled plate is a courtesy rather than the enforcement,
 * exactly as with credits.
 *
 * The banner is not decoration. While the server has no payment provider bound
 * these purchases grant diamonds for free, and a store that took real money
 * would look identical. Saying so is the least the screen can do.
 */
const props = defineProps<{
  shop: PremiumShop;
  wallet: Wallet;
  /**
   * Sold on this tab and for this currency, but built elsewhere on the server —
   * a bundle offer needs the catalogue and the player's shelf, which the wallet
   * has neither of. Hence a second prop rather than a field on `shop`.
   */
  bundles: TankBundle[];
  busyId: string | null;
  /**
   * Which shelf to open on — `Featured` when absent, the ordinary landing.
   * The one other caller is the title bar's diamonds chip: pressing a balance
   * you are short of should open the shelf that sells more of it, not the
   * curated one that might not even be showing a diamond pack.
   */
  initialCategory?: Category;
}>();

const emit = defineEmits<{
  /** The optional second argument pins a limited offer's quote — an id, never a price. */
  buyPack: [id: string, offerId?: string];
  /** The custom-amount pack: no id, just how many diamonds were bought. */
  buyCustomPack: [diamonds: number];
  buyBoost: [id: string];
  buyBundle: [id: string, offerId?: string];
  /**
   * A fuel purchase: one unit, or the tank topped off. The confirmation is
   * asked **here**, in `buyFuel`, because this component holds every tier's
   * price and the wallet's fuel to word it with — the same reason `buyPack`
   * and `buyBundle` ask here rather than one level up.
   */
  refill: [units: FuelRefillKey];
  /** The next extra fuel tank, already confirmed here. */
  buyFuelTank: [];
  /** Experience for diamonds: how many diamonds to spend, never the xp. */
  buyXp: [diamonds: number];
}>();

// ─── The clock ───────────────────────────────────────────────────────────────

/**
 * One ticking now for everything on this tab that counts down — boost
 * remainders and limited-offer deadlines. Every countdown is a subtraction
 * against a served instant; nothing on the wire ticks.
 */
const nowMs = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  timer = setInterval(() => {
    nowMs.value = Date.now();
  }, 1000);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

// ─── Limited offers ──────────────────────────────────────────────────────────

/**
 * An offer is honoured while the *client's* clock says it is open — the same
 * subtraction the countdown flag draws, so the card and its deadline cannot
 * disagree. Once it closes, the card falls back to the standing price the view
 * carries and stops posting the offer id. The server prices every charge
 * against its own clock and refuses a pinned offer that has ended, so the one
 * unreachable outcome is being quoted the sale figure and charged the full one.
 */
function offerOpen(offer: { endsAt: string } | null): boolean {
  return offer !== null && Date.parse(offer.endsAt) > nowMs.value;
}

/** What the plate charges for a pack right now, in minor units. */
function packCents(pack: DiamondPack): number {
  if (!pack.offer || offerOpen(pack.offer)) return pack.priceCents;
  return pack.offer.fullPriceCents;
}

/** What the plate charges for a bundle right now, in diamonds. */
function bundleCost(bundle: TankBundle): number {
  if (!bundle.offer || offerOpen(bundle.offer)) return bundle.diamondCost;
  return bundle.offer.fullDiamondCost;
}

/** `30` for 30% off — derived from the two served figures, never re-served. */
function offerPct(current: number, full: number): number {
  return Math.round((1 - current / full) * 100);
}

// ─── The rail ────────────────────────────────────────────────────────────────

export type Category = 'featured' | 'diamonds' | 'bundles' | 'boosts';

const CATEGORIES: Array<{ id: Category; label: string }> = [
  { id: 'featured', label: 'Featured' },
  { id: 'diamonds', label: 'Diamonds' },
  // The id stayed `bundles` — it is what the e2e harness and the wire both
  // still call it (`data-bundle`, `bundleCost`, `TankBundle`) — only the
  // label read by a player changed, to match what the card actually is.
  { id: 'bundles', label: 'Tanks' },
  { id: 'boosts', label: 'Boosts' },
];

const cat = ref<Category>(props.initialCategory ?? 'featured');

/** Featured is the one pane that renders its cards at hero scale. */
const hero = computed(() => cat.value === 'featured');

/**
 * What each pane shows, computed rather than duplicated: the card markup for a
 * pack, a bundle and a boost exists once in the template, and these decide
 * which instances are on the current shelf.
 *
 * One shelf model for bundles and packs together, because two shelves are
 * genuinely mixed now: Featured's *Limited offers* row holds every discounted
 * bundle **and** every discounted pack in one grid, side by side, rather than
 * as two stacked single-item rows. A shelf that carries only one kind simply
 * leaves the other array empty; the card markup still exists exactly once per
 * kind, or the copies drift and the e2e cases catch only whichever one they
 * were written against.
 *
 * A bundle under a live offer stays on the Tanks pane's full list too — the
 * original one-card-one-price rule that pulled it off the regular shelf is
 * deprecated: the card itself already carries the struck standing price, the
 * deal flag and the discounted plate wherever it renders, so there is no
 * second price to mislead with, only a second place to find the same deal.
 * The lists are re-derived from the ticking clock, so an offer expiring under
 * an open tab leaves Featured without a refresh.
 */
interface Shelf {
  key: string;
  title: string;
  bundles: TankBundle[];
  packs: DiamondPack[];
}

const offeredBundles = computed(() => props.bundles.filter((b) => offerOpen(b.offer)));
const offeredPacks = computed(() => props.shop.packs.filter((p) => offerOpen(p.offer)));

const shelves = computed<Shelf[]>(() => {
  const list: Shelf[] = [];
  if (cat.value === 'featured') {
    // Only the limited-offer bundle reaches Featured; the full-price bundles
    // live on their own Tanks pane and nowhere else. Featured used to carry
    // them all, back when it was the only pane the e2e harness (and a player
    // following a link) could count on — the Tanks pane owns that job now,
    // and a landing pane that lists a whole catalogue is not a curation.
    list.push({
      key: 'limited',
      title: 'Limited offers',
      bundles: offeredBundles.value,
      packs: offeredPacks.value,
    });
    list.push({
      key: 'packs',
      title: 'Diamonds',
      bundles: [],
      packs: props.shop.packs.filter((p) => p.highlight && !offerOpen(p.offer)),
    });
  }
  if (cat.value === 'bundles') {
    // The whole roster, discounted cards included — see the note above.
    list.push({ key: 'bundles', title: 'Tank bundles', bundles: props.bundles, packs: [] });
  }
  if (cat.value === 'diamonds') {
    // The whole ladder, discounted card included — its price and flag already
    // say what the deal is, and hiding it here would make the ladder read as
    // having a rung missing.
    list.push({ key: 'packs', title: 'Diamonds', bundles: [], packs: props.shop.packs });
  }
  return list.filter((s) => s.bundles.length + s.packs.length > 0);
});

/** Experience and credit boosts — the four-across row. */
const visibleBoosts = computed<BoostOffer[]>(() => {
  const earning = props.shop.boosts.filter((b) => b.kind !== 'RESEARCH');
  if (cat.value === 'boosts') return earning;
  // The weekly boosts are the featured pair: they are the better rate, and a
  // player who buys boosts at all buys them weekly.
  if (cat.value === 'featured') return earning.filter((b) => b.hours >= 168);
  return [];
});

/** The Research Lab boosts — their own three-across row on the Boosts tab only. */
const researchBoosts = computed<BoostOffer[]>(() =>
  cat.value === 'boosts' ? props.shop.boosts.filter((b) => b.kind === 'RESEARCH') : [],
);

/** The shelves the boost cards are drawn in: a heading, a row class and the offers. */
const boostShelves = computed(() =>
  [
    { key: 'earn', title: 'Boosts', rowClass: 'quad', offers: visibleBoosts.value },
    { key: 'lab', title: 'Research lab', rowClass: '', offers: researchBoosts.value },
  ].filter((shelf) => shelf.offers.length > 0),
);

/** `24` → `24h`, `168` → `1 week`: a boost is sold in days past the first. */
function boostSpan(hours: number): string {
  if (hours >= 168 && hours % 168 === 0) return hours === 168 ? '1 week' : `${hours / 168} weeks`;
  return `${hours}h`;
}

/** What a boost does, in the words a card and its confirmation share. */
function boostSummary(boost: BoostOffer): string {
  const span = boostSpan(boost.hours);
  if (boost.kind === 'XP') return `EXP ×${boost.multiplier} for ${span}`;
  if (boost.kind === 'LOOT') return `Credits ×${boost.multiplier} for ${span}`;
  return `Research time ÷${boost.multiplier} for ${span}`;
}

// ─── Store art ───────────────────────────────────────────────────────────────

/**
 * `pack_field` → `/assets/store/pack-field.webp` — the same id-derived
 * convention part, map and research art follow, so a new pack gets its picture
 * by existing rather than by somebody remembering a second string.
 */
function artUrl(id: string): string {
  return `/assets/store/${id.replace(/_/g, '-')}.webp`;
}

/** Cards whose art 404'd; they keep their dark plate and their label. */
const artFailed = ref<Record<string, boolean>>({});

function artMissed(id: string): void {
  artFailed.value[id] = true;
}

/**
 * A store offer's art as a confirmation-dialog field, or nothing at all.
 *
 * Spread into the request so an offer whose image is missing contributes no `art`
 * key and the dialog takes its text-only shape. It reads `artFailed`, which is the
 * *card's* record of a 404 — one source of truth for what is on disk, rather than
 * the modal discovering the same missing file a second time.
 */
function storeArt(id: string): { art?: { kind: 'image'; src: string } } {
  return artFailed.value[id] ? {} : { art: { kind: 'image', src: artUrl(id) } };
}

/**
 * Fuel art, two tiers deep.
 *
 * The fill card keeps the original `fuel_refill` picture; the one-unit card
 * tries `fuel_refill_1` first and — if that image is missing — falls back to
 * sharing `fuel_refill`'s picture rather than dropping straight to the dark
 * plate. Only if *that* 404s too does the ordinary `artFailed` mechanism take
 * over and draw the plain plate.
 */
const fuelArtFallenBack = ref<Record<string, boolean>>({});

function fuelArtId(tier: FuelRefillTier): string {
  if (tier.units === 'full' || fuelArtFallenBack.value[tier.units]) return 'fuel_refill';
  return `fuel_refill_${tier.units}`;
}

function fuelArtMissed(tier: FuelRefillTier): void {
  if (tier.units !== 'full' && !fuelArtFallenBack.value[tier.units]) {
    fuelArtFallenBack.value = { ...fuelArtFallenBack.value, [tier.units]: true };
    return;
  }
  artMissed(fuelArtId(tier));
}

// ─── Money, wording ──────────────────────────────────────────────────────────

const busy = computed(() => props.busyId !== null);

function remaining(expiresAt: string | null): string | null {
  return boostRemaining(expiresAt, nowMs.value);
}

/**
 * Three of the spends here ask in this component, and they are the ones whose
 * numbers live only in this component's props.
 *
 * **A pack is the only purchase in the game paid for in real money**, and the
 * shared request carries `'credits' | 'diamonds'` and nothing else. Rather than
 * teach one dialog to format currencies and minor units for the single spend
 * that is not in a game currency, the money price is worded here — by
 * `formatPrice`, which the plate beside it already uses — and handed over as
 * `priceLabel`, which the dialog renders **in place of** the number and unit.
 *
 * That last part matters and was got wrong first. Passing the diamond total as
 * the price put the figure the player *receives* in the dialog's largest text
 * and the figure they *pay* in the small print, which is the shape of a dark
 * pattern whatever the intent. The headline is always what leaves your pocket;
 * what arrives is the detail line.
 */
async function buyPack(pack: DiamondPack): Promise<void> {
  // The offer is read once, at the moment of asking, and rides the emit as an
  // id — the server refuses it if the window closed between here and there.
  const offer = pack.offer && offerOpen(pack.offer) ? pack.offer : null;
  const cents = packCents(pack);
  const money = formatPrice(cents, pack.currency);
  const detail = [
    `Pays out ${pack.total.toLocaleString('en-GB')} diamonds`,
    offer
      ? `Limited offer — ${offerPct(cents, offer.fullPriceCents)}% off ` +
        `${formatPrice(offer.fullPriceCents, pack.currency)}, ` +
        `ends in ${remaining(offer.endsAt) ?? 'moments'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const accepted = await askToBuy({
    item: pack.name,
    detail,
    // The card's own picture, through the same `artUrl`, so the dialog shows the
    // pile of crystals the player just clicked on. Skipped when that file already
    // 404'd for this card — the dialog inherits the shop's knowledge of what is on
    // disk rather than re-testing it.
    ...storeArt(pack.id),
    price: cents,
    priceLabel: money,
    currency: 'diamonds',
    confirmLabel: `Pay ${money}`,
  });
  if (accepted) emit('buyPack', pack.id, offer?.id);
}

// ─── Custom crystal amount ───────────────────────────────────────────────────

/**
 * The custom-amount pack: a slider rather than a ladder, for whoever wants
 * neither the pack below nor the pack above.
 *
 * `shop.customPack` is optional — an older server that has not shipped this
 * yet simply omits it, and the card is absent rather than broken. Everywhere
 * this reads it, it does so behind a null check or a `v-if`; there is no
 * silent default standing in for a config the server never sent.
 */
/** Where the slider rests before the player touches it (clamped on use like any typed figure). */
const DEFAULT_CUSTOM_DIAMONDS = 1000;
const customDiamonds = ref(DEFAULT_CUSTOM_DIAMONDS);

/** Clamped to whatever the *current* config allows, in case it ever changes. */
const customConfig = computed(() => props.shop.customPack ?? null);

/**
 * The amount actually priced, confirmed and sent — the raw ref clamped to the
 * served bounds and snapped to the served step. The number input accepts any
 * keystroke (149, 80,399, an emptied field), and the server refuses anything
 * that is not a step multiple inside the bounds, so pricing the raw figure
 * would quote a spend the server then rejects — the one outcome a plate must
 * never offer. `maxDiamonds` is itself a step multiple (the server derives it
 * by flooring the price cap to the step), so rounding a clamped value to the
 * nearest step can never step past the cap.
 */
const customAmount = computed(() => {
  const cfg = customConfig.value;
  if (!cfg) return 0;
  const raw = Number.isFinite(customDiamonds.value) ? customDiamonds.value : cfg.minDiamonds;
  const clamped = Math.min(cfg.maxDiamonds, Math.max(cfg.minDiamonds, raw));
  return Math.round(clamped / cfg.stepDiamonds) * cfg.stepDiamonds;
});

/** Money for the snapped amount, at the shop's own rate. Zero with no config. */
const customPriceCents = computed(() => {
  const cfg = customConfig.value;
  if (!cfg) return 0;
  return customPriceFor(customAmount.value, cfg.rateCentsPer100);
});

/**
 * Rounded **up** to the cent, like the server (`customPackPriceCents`): 1.99 per
 * hundred is not a whole cent for most counts, and the charge is never lower
 * than the rate. The tiny epsilon only guards float noise on a converted rate.
 */
function customPriceFor(diamonds: number, rateCentsPer100: number): number {
  return Math.ceil((diamonds * rateCentsPer100) / 100 - 1e-9);
}

/**
 * The slider is logarithmic: one linear track from a few hundred to half a
 * million diamonds would put every ordinary purchase inside its first pixels.
 * The number box beside it is the exact control; this is the coarse one.
 */
const SLIDER_STEPS = 1000;
const customSlider = computed({
  get(): number {
    const cfg = customConfig.value;
    if (!cfg || cfg.maxDiamonds <= cfg.minDiamonds) return 0;
    const ratio = Math.log(customAmount.value / cfg.minDiamonds) / Math.log(cfg.maxDiamonds / cfg.minDiamonds);
    return Math.round(ratio * SLIDER_STEPS);
  },
  set(position: number) {
    const cfg = customConfig.value;
    if (!cfg) return;
    const t = Math.min(1, Math.max(0, position / SLIDER_STEPS));
    customDiamonds.value = Math.round(cfg.minDiamonds * Math.pow(cfg.maxDiamonds / cfg.minDiamonds, t));
  },
});

/** A typed figure is shown as what will actually be sold: clamped, whole diamonds. */
function settleCustomInput(): void {
  customDiamonds.value = customAmount.value;
}

/**
 * Same discipline as `buyPack`: the headline is always what is *paid*, so the
 * plate and the confirmation both lead with the money price and carry the
 * diamond total as the detail — never the other way round.
 */
async function buyCustomPack(): Promise<void> {
  const cfg = customConfig.value;
  if (!cfg) return;
  // The snapped figure is what was priced, so it is the only figure that may
  // ride the emit — a dialog that quoted one amount and sent another would be
  // the pack dark-pattern's quieter sibling.
  const diamonds = customAmount.value;
  const cents = customPriceCents.value;
  const money = formatPrice(cents, cfg.currency);
  const accepted = await askToBuy({
    item: `${diamonds.toLocaleString('en-GB')} diamonds`,
    detail: `${formatPrice(cfg.rateCentsPer100, cfg.currency)} per 100 diamonds · custom amount`,
    price: cents,
    priceLabel: money,
    currency: 'diamonds',
    confirmLabel: `Pay ${money}`,
  });
  if (accepted) emit('buyCustomPack', diamonds);
}

// ─── Experience for diamonds ─────────────────────────────────────────────────

/**
 * The one sink on this tab that sells *progress* rather than a thing.
 *
 * Same slider shape as the custom pack above and for the same reason — the
 * amount is the player's choice — but it is spending diamonds rather than money,
 * so the plate's headline is a diamond figure and the rate is served (see
 * `XpPurchaseConfig`). Optional config, absent card: a server that has not
 * shipped this omits `xpPurchase` and nothing renders.
 */
const xpDiamonds = ref(props.shop.xpPurchase?.minDiamonds ?? 1);

const xpConfig = computed(() => props.shop.xpPurchase ?? null);

/**
 * The slider's own ceiling: the served cap, or the balance when that is smaller.
 *
 * Unlike the custom pack — which buys diamonds with money and so has no balance
 * to respect — every diamond spent here is one the player is holding, so a slider
 * that ranged over the served cap would spend most of its travel quoting spends
 * the server would refuse. Never below the minimum, or the range collapses and
 * the input stops accepting anything.
 */
const xpMaxDiamonds = computed(() => {
  const cfg = xpConfig.value;
  if (!cfg) return 0;
  return Math.max(cfg.minDiamonds, Math.min(cfg.maxDiamonds, props.wallet.diamonds));
});

/** The amount actually priced, confirmed and sent — clamped and step-snapped. */
const xpAmount = computed(() => {
  const cfg = xpConfig.value;
  if (!cfg) return 0;
  const raw = Number.isFinite(xpDiamonds.value) ? xpDiamonds.value : cfg.minDiamonds;
  const clamped = Math.min(xpMaxDiamonds.value, Math.max(cfg.minDiamonds, raw));
  const snapped = Math.round(clamped / cfg.stepDiamonds) * cfg.stepDiamonds;
  return Math.min(xpMaxDiamonds.value, Math.max(cfg.minDiamonds, snapped));
});

/** XP for the snapped amount, at the served rate. The server re-derives it. */
const xpGain = computed(() => Math.floor(xpAmount.value * (xpConfig.value?.xpPerDiamond ?? 0)));

/** False when the balance cannot even reach the minimum purchase. */
const canBuyXp = computed(() => {
  const cfg = xpConfig.value;
  return cfg !== null && props.wallet.diamonds >= cfg.minDiamonds;
});

/**
 * Whether this purchase would fill the current rank — the only rank claim the
 * client is entitled to make.
 *
 * `xpForNextRank` is the current rank's *whole span* and `xpIntoRank` is the
 * progress into it, so the remainder is a subtraction of two served figures. What
 * cannot be said is how many ranks a large purchase crosses: that needs the curve,
 * which is the server's, and the result screen's promotion bar draws exactly this
 * line for exactly this reason.
 */
const xpPromotes = computed(() => {
  const span = props.wallet.xpForNextRank;
  if (span === null) return false;
  return xpGain.value >= span - props.wallet.xpIntoRank;
});

async function buyXp(): Promise<void> {
  const cfg = xpConfig.value;
  if (!cfg || !canBuyXp.value) return;
  // The snapped figure is what was priced, so it is the only figure that may ride
  // the emit — the same rule the custom pack follows one card up.
  const diamonds = xpAmount.value;
  const xp = xpGain.value;
  const rate = `${cfg.xpPerDiamond.toLocaleString('en-GB')} XP per diamond`;
  const accepted = await askToBuy({
    // The reward is the item and the diamonds are the price, which is the way
    // round the headline rule wants: what is *paid* is the big figure.
    item: `${xp.toLocaleString('en-GB')} experience`,
    detail: xpPromotes.value ? `${rate} · reaches the next rank` : rate,
    price: diamonds,
    currency: 'diamonds',
  });
  if (accepted) emit('buyXp', diamonds);
}

/**
 * **A boost is bought over and over.** It is the one purchase here a player
 * makes weekly rather than once, so the plate and the confirmation say
 * *Extend* while one is running — a dialog offering to "buy" something the
 * player can see is already active reads as a mistake about to be made.
 */
async function buyBoost(boost: BoostOffer): Promise<void> {
  const verb = boost.active ? 'Extend' : 'Activate';
  const accepted = await askToBuy({
    item: boost.name,
    detail: `${verb} · ${boostSummary(boost)}`,
    ...storeArt(boost.id),
    price: boost.diamondCost,
    currency: 'diamonds',
    confirmLabel: verb,
  });
  if (accepted) emit('buyBoost', boost.id);
}

/**
 * What a bundle contains, worded once and used in three places.
 *
 * The card's contents line, the detail line of the confirmation and the report
 * a person reads afterwards all have to agree, and the interesting half is what
 * happens when a player already owns one of the two. The price does not change
 * — see the server's `purchaseBundle` for why that is the right answer — so the
 * only honest thing to do is *say so before the click*, in both places, in the
 * same words.
 *
 * A dialog that quoted 380 💎 for "a Marauder and a Buckshot" to somebody who
 * already had the Marauder would be accurate and still a swindle.
 */
function bundleContents(bundle: TankBundle): string {
  const parts = [
    `${bundle.hullName} hull${bundle.ownsHull ? ' (already owned)' : ''}`,
    `${bundle.turretName} turret${bundle.ownsTurret ? ' (already owned)' : ''}`,
  ];
  return parts.join(' · ');
}

async function buyBundle(bundle: TankBundle): Promise<void> {
  const offer = bundle.offer && offerOpen(bundle.offer) ? bundle.offer : null;
  const cost = bundleCost(bundle);
  const detail = [
    bundleContents(bundle),
    offer
      ? `Limited offer — ${offerPct(cost, offer.fullDiamondCost)}% off ` +
        `${offer.fullDiamondCost} diamonds, ends in ${remaining(offer.endsAt) ?? 'moments'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const accepted = await askToBuy({
    item: bundle.name,
    detail,
    // The two items assembled — a bundle's product is the *pairing*, so the
    // `'tank'` framing is the only honest picture of it, and it is the same still
    // the card renders.
    art: { kind: 'model', hullId: bundle.hullId, turretId: bundle.turretId, framing: 'tank' },
    price: cost,
    currency: 'diamonds',
  });
  if (accepted) emit('buyBundle', bundle.id, offer?.id);
}

// ─── Fuel ────────────────────────────────────────────────────────────────────

/** `+1 unit`, or `Full tank` for the top-off. */
function fuelTierName(tier: FuelRefillTier): string {
  return tier.units === 'full' ? 'Full tank' : '+1 unit';
}

function fuelTierBlurb(tier: FuelRefillTier): string {
  if (tier.units === 'full') {
    return `Fill the tank to ${props.shop.fuelRefill.capacity} now, at a discount per unit, instead of waiting an hour a unit.`;
  }
  return 'One unit now, for the sortie you are about to fly.';
}

/**
 * Asks for a fuel purchase and emits the units bought.
 *
 * Asked here rather than by `GarageScreen`, now that there are three tiers
 * rather than one: this component already holds `wallet.fuel`, the shop's
 * capacity and every tier's own price, which is what a confirmation needs to
 * word itself — `GarageScreen` has none of those and would only be relaying a
 * question this card already knows how to ask. Same discipline `buyPack` and
 * `buyBundle` already follow.
 */
async function buyFuel(tier: FuelRefillTier): Promise<void> {
  const capacity = props.shop.fuelRefill.capacity;
  const after = tier.units === 'full' ? capacity : Math.min(props.wallet.fuel + 1, capacity);
  const accepted = await askToBuy({
    item: fuelTierName(tier),
    detail: `Fills the tank to ${after}, from ${props.wallet.fuel}`,
    // `fuelArtId` is the tier's own two-deep fallback (its own picture, then the
    // shared drum), so the dialog shows whichever the card settled on.
    ...storeArt(fuelArtId(tier)),
    price: tier.diamondCost,
    currency: 'diamonds',
    confirmLabel: 'Refill',
  });
  if (accepted) emit('refill', tier.units);
}

/** The extra-tank card: owned so far, and the next one's price (null at nine). */
const tanks = computed(() => props.shop.fuelTanks);

async function buyFuelTank(): Promise<void> {
  const offer = tanks.value;
  if (!offer || offer.nextDiamondCost === null) return;
  const accepted = await askToBuy({
    item: 'Extra fuel tank',
    detail: `Tank ${offer.owned + 1} of ${offer.max}: +${offer.unitsPerTank} capacity, for good (${props.wallet.fuelCapacity} → ${props.wallet.fuelCapacity + offer.unitsPerTank})`,
    ...storeArt('fuel_tank'),
    price: offer.nextDiamondCost,
    currency: 'diamonds',
    confirmLabel: 'Fit tank',
  });
  if (accepted) emit('buyFuelTank');
}
</script>

<template>
  <div class="store">
    <nav class="rail" aria-label="Store sections">
      <button
        v-for="entry in CATEGORIES"
        :key="entry.id"
        type="button"
        :data-test="`store-cat-${entry.id}`"
        :class="{ active: cat === entry.id }"
        @click="cat = entry.id"
      >
        {{ entry.label }}
      </button>
    </nav>

    <div class="pane">
      <p v-if="shop.paymentsStubbed" class="stub-banner" data-test="payments-stubbed">
        <strong>No payment provider is connected.</strong>
        Buying a pack here credits the diamonds without charging anything — this is
        a development stub, and the server refuses it in production.
      </p>

      <section v-for="shelf in shelves" :key="shelf.key" class="shelf">
        <h3>{{ shelf.title }}</h3>
        <div class="cards" :class="{ wide: shelf.bundles.length > 0, hero }">
          <article
            v-for="bundle in shelf.bundles"
            :key="bundle.id"
            class="card bundle"
            :class="{ complete: !bundle.available, deal: offerOpen(bundle.offer), hero }"
            :data-bundle="bundle.id"
          >
            <div class="art">
              <ItemPreview
                class="shot"
                :hull-id="bundle.hullId"
                :turret-id="bundle.turretId"
                framing="tank"
                :label="`${bundle.hullName} with ${bundle.turretName}`"
                eager
              />
              <div class="label">
                <span class="eyebrow">
                  {{ offerOpen(bundle.offer) ? 'Limited offer' : 'Tank bundle' }}
                </span>
                <h4 class="big">{{ bundle.name }}</h4>
                <em v-if="bundle.offer && offerOpen(bundle.offer)" class="sub">
                  <s>{{ bundle.offer.fullDiamondCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></s>
                  <span class="pct">−{{ offerPct(bundleCost(bundle), bundle.offer.fullDiamondCost) }}%</span>
                </em>
              </div>
              <span v-if="hero" class="flag featured">Featured</span>
              <span v-if="!bundle.available" class="flag owned">Owned</span>
              <span
                v-else-if="bundle.offer && offerOpen(bundle.offer)"
                class="flag deal"
                :data-test="`${bundle.id}-offer`"
              >
                Ends in {{ remaining(bundle.offer.endsAt) ?? 'moments' }}
              </span>
            </div>

            <div class="body">
              <p class="contents" :data-test="`${bundle.id}-contents`">
                {{ bundleContents(bundle) }}
              </p>
              <p class="blurb">{{ bundle.blurb }}</p>

              <p
                v-if="bundle.available && (bundle.ownsHull || bundle.ownsTurret)"
                class="part-owned"
              >
                You already have the {{ bundle.ownsHull ? bundle.hullName : bundle.turretName }}.
                The price is for the bundle, so this buys the
                {{ bundle.ownsHull ? bundle.turretName : bundle.hullName }} alone.
              </p>
            </div>

            <button
              type="button"
              class="plate"
              :data-action="`buy-${bundle.id}`"
              :disabled="busy || !bundle.available || wallet.diamonds < bundleCost(bundle)"
              @click="buyBundle(bundle)"
            >
              <template v-if="!bundle.available">In the shed</template>
              <template v-else-if="bundleCost(bundle) === 0">Free</template>
              <template v-else>Buy · {{ bundleCost(bundle) }} <CurrencyIcon kind="diamonds" /></template>
            </button>
          </article>

          <article
            v-for="pack in shelf.packs"
            :key="pack.id"
            class="card"
            :class="{ highlight: pack.highlight, deal: offerOpen(pack.offer), hero }"
            :data-pack="pack.id"
            :title="pack.blurb"
          >
            <div class="art">
              <img
                v-if="!artFailed[pack.id]"
                :src="artUrl(pack.id)"
                alt=""
                loading="lazy"
                @error="artMissed(pack.id)"
              />
              <div class="label">
                <span class="eyebrow"><CurrencyIcon kind="diamonds" :labelled="false" /> {{ pack.name }}</span>
                <strong class="big gem">{{ pack.total.toLocaleString('en-GB') }}</strong>
                <em v-if="pack.bonusDiamonds > 0" class="sub">
                  {{ pack.diamonds.toLocaleString('en-GB') }} + {{ pack.bonusDiamonds }} bonus
                </em>
                <em v-if="pack.offer && offerOpen(pack.offer)" class="sub">
                  <s>{{ formatPrice(pack.offer.fullPriceCents, pack.currency) }}</s>
                  <span class="pct">−{{ offerPct(packCents(pack), pack.offer.fullPriceCents) }}%</span>
                </em>
              </div>
              <span v-if="hero" class="flag featured">Featured</span>
              <span
                v-if="pack.offer && offerOpen(pack.offer)"
                class="flag deal"
                :data-test="`${pack.id}-offer`"
              >
                Ends in {{ remaining(pack.offer.endsAt) ?? 'moments' }}
              </span>
              <span v-else-if="pack.highlight" class="flag">Popular</span>
            </div>
            <button
              type="button"
              class="plate"
              :data-action="`buy-${pack.id}`"
              :disabled="busy"
              @click="buyPack(pack)"
            >
              <template v-if="packCents(pack) === 0">Free</template>
              <template v-else>{{ formatPrice(packCents(pack), pack.currency) }}</template>
            </button>
          </article>
        </div>
      </section>

      <section v-if="cat === 'diamonds' && customConfig" class="shelf">
        <h3>Custom amount</h3>
        <div class="cards">
          <article class="banner" data-use="custom-pack">
            <div class="banner-art">
              <img
                v-if="!artFailed['custom_pack']"
                :src="artUrl('custom_pack')"
                alt=""
                loading="lazy"
                @error="artMissed('custom_pack')"
              />
              <span v-else class="banner-mark" aria-hidden="true">
                <CurrencyIcon kind="diamonds" :labelled="false" />
              </span>
            </div>

            <div class="banner-body">
              <div class="banner-figure">
                <b class="banner-money" data-test="custom-money">
                  {{ formatPrice(customPriceCents, customConfig.currency) }}
                </b>
                <span class="banner-count" data-test="custom-count">
                  {{ customAmount.toLocaleString('en-GB') }} <CurrencyIcon kind="diamonds" />
                </span>
                <span class="banner-rate">
                  Minimum
                  {{ formatPrice(customPriceFor(customConfig.minDiamonds, customConfig.rateCentsPer100), customConfig.currency) }}
                  = {{ customConfig.minDiamonds.toLocaleString('en-GB') }} <CurrencyIcon kind="diamonds" />
                </span>
              </div>

              <div class="banner-track">
                <input
                  type="range"
                  class="custom-slider"
                  data-action="custom-amount"
                  min="0"
                  :max="SLIDER_STEPS"
                  step="1"
                  v-model.number="customSlider"
                  :disabled="busy"
                  aria-label="Diamonds (coarse)"
                />
                <label class="custom-number">
                  <input
                    type="number"
                    data-action="custom-amount-input"
                    :min="customConfig.minDiamonds"
                    :max="customConfig.maxDiamonds"
                    step="1"
                    v-model.number="customDiamonds"
                    :disabled="busy"
                    @change="settleCustomInput"
                  />
                  <span><CurrencyIcon kind="diamonds" :labelled="false" /></span>
                </label>
              </div>

              <button
                type="button"
                class="plate"
                data-action="buy-custom-pack"
                :disabled="busy"
                @click="buyCustomPack()"
              >
                Buy
              </button>
            </div>
          </article>
        </div>
      </section>

      <section v-if="cat === 'boosts'" class="shelf">
        <h3>Fuel</h3>
        <div class="cards fuel-tiers">
          <article
            v-for="tier in shop.fuelRefill.tiers"
            :key="tier.units"
            class="card fuel-card"
            :class="{ running: tier.units === 'full' }"
            :data-use="tier.units === 'full' ? 'fuel' : `fuel-${tier.units}`"
          >
            <div class="art">
              <img
                v-if="!artFailed[fuelArtId(tier)]"
                :src="artUrl(fuelArtId(tier))"
                alt=""
                loading="lazy"
                @error="fuelArtMissed(tier)"
              />
              <div class="label">
                <span class="eyebrow">Fuel</span>
                <strong class="big name">{{ fuelTierName(tier) }}</strong>
                <em class="sub">{{ wallet.fuel }} / {{ wallet.fuelCapacity }} in the tank</em>
              </div>
            </div>
            <div class="body">
              <p class="blurb">{{ fuelTierBlurb(tier) }}</p>
            </div>
            <button
              type="button"
              class="plate"
              :data-action="tier.units === 'full' ? 'buy-fuel-refill' : `buy-fuel-${tier.units}`"
              :disabled="busy || !tier.available || wallet.diamonds < tier.diamondCost"
              @click="buyFuel(tier)"
            >
              <template v-if="!tier.available">Tank full</template>
              <template v-else-if="tier.diamondCost === 0">{{ fuelTierName(tier) }} · Free</template>
              <template v-else>{{ fuelTierName(tier) }} · {{ tier.diamondCost }} <CurrencyIcon kind="diamonds" /></template>
            </button>
          </article>

          <article v-if="tanks" class="card fuel-card" data-use="fuel-tank">
            <div class="art">
              <img
                v-if="!artFailed['fuel_tank']"
                :src="artUrl('fuel_tank')"
                alt=""
                loading="lazy"
                @error="artMissed('fuel_tank')"
              />
              <div class="label">
                <span class="eyebrow">Fuel</span>
                <strong class="big name">Extra tank</strong>
                <em class="sub">{{ tanks.owned }} / {{ tanks.max }} tanks fitted</em>
              </div>
            </div>
            <div class="body">
              <p class="blurb">
                +{{ tanks.unitsPerTank }} fuel capacity, for good. More sorties before you wait; the
                tank still refills one unit an hour.
              </p>
            </div>
            <button
              type="button"
              class="plate"
              data-action="buy-fuel-tank"
              :disabled="busy || tanks.nextDiamondCost === null || wallet.diamonds < tanks.nextDiamondCost"
              @click="buyFuelTank"
            >
              <template v-if="tanks.nextDiamondCost === null">All tanks fitted</template>
              <template v-else>Extra tank · {{ tanks.nextDiamondCost }} <CurrencyIcon kind="diamonds" /></template>
            </button>
          </article>
        </div>
      </section>

      <section v-for="shelf in boostShelves" :key="shelf.key" class="shelf">
        <h3>{{ shelf.title }}</h3>
        <div class="cards" :class="[shelf.rowClass, { hero }]">
          <article
            v-for="boost in shelf.offers"
            :key="boost.id"
            class="card"
            :class="{ running: boost.active, hero }"
            :data-use="boost.id"
            :title="boost.blurb"
          >
            <div class="art">
              <img
                v-if="!artFailed[boost.id]"
                :src="artUrl(boost.id)"
                alt=""
                loading="lazy"
                @error="artMissed(boost.id)"
              />
              <div class="label">
                <strong class="big name">{{ boost.name }}</strong>
                <em class="sub boost-effect" :data-kind="boost.kind" :data-test="`${boost.id}-effect`">
                  <template v-if="boost.kind === 'XP'"><b>EXP</b> ×{{ boost.multiplier }} for {{ boostSpan(boost.hours) }}</template>
                  <template v-else-if="boost.kind === 'LOOT'">
                    <span class="coin-reveal"
                      ><CurrencyIcon kind="credits" :labelled="false" /><span class="coin-word">credits</span></span
                    >
                    ×{{ boost.multiplier }} for {{ boostSpan(boost.hours) }}
                  </template>
                  <template v-else><b>Research time</b> ÷{{ boost.multiplier }} for {{ boostSpan(boost.hours) }}</template>
                </em>
              </div>
              <span v-if="hero" class="flag featured">Featured</span>
              <span v-if="boost.active" class="flag active" :data-test="`${boost.id}-remaining`">
                {{ remaining(boost.expiresAt) ?? 'active' }} left
              </span>
            </div>
            <button
              type="button"
              class="plate"
              :data-action="`buy-${boost.id}`"
              :disabled="busy || wallet.diamonds < boost.diamondCost"
              @click="buyBoost(boost)"
            >
              <template v-if="boost.diamondCost === 0">{{ boost.active ? 'Extend' : 'Activate' }} · Free</template>
              <template v-else>{{ boost.active ? 'Extend' : 'Activate' }} · {{ boost.diamondCost }} <CurrencyIcon kind="diamonds" /></template>
            </button>
          </article>
        </div>
      </section>

      <section v-if="cat === 'boosts' && xpConfig" class="shelf">
        <h3>Experience</h3>
        <div class="cards">
          <article class="banner" data-use="xp-purchase">
            <div class="banner-art">
              <img
                v-if="!artFailed['xp_purchase']"
                :src="artUrl('xp_purchase')"
                alt=""
                loading="lazy"
                @error="artMissed('xp_purchase')"
              />
              <span v-else class="banner-mark" aria-hidden="true">
                <CurrencyIcon kind="diamonds" :labelled="false" />
              </span>
            </div>

            <div class="banner-body">
              <div class="banner-figure">
                <b class="banner-money" data-test="xp-cost">
                  {{ xpAmount.toLocaleString('en-GB') }}
                  <CurrencyIcon kind="diamonds" :labelled="false" />
                </b>
                <span class="banner-count" data-test="xp-gain">
                  {{ xpGain.toLocaleString('en-GB') }} XP
                </span>
                <span class="banner-rate">
                  {{ xpConfig.xpPerDiamond.toLocaleString('en-GB') }} XP per
                  <CurrencyIcon kind="diamonds" :labelled="false" />
                </span>
              </div>

              <div class="banner-track">
                <input
                  type="range"
                  class="custom-slider"
                  data-action="xp-amount"
                  :min="xpConfig.minDiamonds"
                  :max="xpMaxDiamonds"
                  :step="xpConfig.stepDiamonds"
                  v-model.number="xpDiamonds"
                  :disabled="busy || !canBuyXp"
                />
                <label class="custom-number">
                  <input
                    type="number"
                    :min="xpConfig.minDiamonds"
                    :max="xpMaxDiamonds"
                    :step="xpConfig.stepDiamonds"
                    v-model.number="xpDiamonds"
                    :disabled="busy || !canBuyXp"
                  />
                  <span><CurrencyIcon kind="diamonds" :labelled="false" /></span>
                </label>
              </div>

              <!-- Always drawn, empty or not. See `.banner-note` in the styles. -->
              <p
                class="banner-note"
                data-test="xp-purchase-state"
                :data-promotes="xpPromotes ? 'yes' : 'no'"
              >
                <em v-if="xpPromotes" class="promotes">Reaches the next rank.</em>
              </p>

              <button
                type="button"
                class="plate"
                data-action="buy-xp"
                :disabled="busy || !canBuyXp"
                @click="buyXp()"
              >
                <template v-if="canBuyXp">Buy</template>
                <template v-else>No diamonds</template>
              </button>
            </div>
          </article>
        </div>
      </section>

    </div>
  </div>
</template>

<style scoped>
.store {
  /*
   * One type-scale factor for the whole storefront, the title bar's
   * `--bar-scale` idiom: the geometry already scales with the window (the
   * container is 70vw and every card track is a fraction of the pane), so on a
   * big display the cards grow on their own — this is what stops the type
   * staying laptop-sized inside them. `1` at every Mac width, so nothing a
   * laptop shows moves; `1.25` from 2000px up, the same tier the title bar
   * enlarges at. Every merchandise-facing font size below multiplies by it;
   * the rail, the stub banner and the flags stay chrome-sized on purpose.
   */
  --shop-scale: 1;
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
  /*
   * 1rem again. It was **3.25rem** to clear `MusicControl`, which pinned a mute
   * chip to the window's bottom centre — without the clearance the last plate on
   * a pane sat under that chip and a buy button became a music toggle. The chip
   * is gone from every viewport (owner instruction, 2026-09-02; the control lives
   * in the settings window's Audio group, which also states its `M` key), so what
   * is left here is 52px of dead space at the foot of a scrolling shop pane —
   * which on a phone is most of a plate. Kept as a paragraph rather than deleted
   * because the *reason* a shop pane needs a bottom clearance is worth knowing if
   * anything is ever pinned to the bottom centre again.
   */
  padding-bottom: 1rem;
  width: 100%;
  /*
   * The store reads as a browsed catalogue rather than a document, and a
   * catalogue that stretches to a 27" monitor's full width is one nobody can
   * scan — the cards would be nearer wallpaper than a shelf. Capped at 70% of
   * the viewport and centred, so roughly 15% of background shows either side
   * on a wide window.
   *
   * The floor is `min(56rem, 100%)` rather than a bare `56rem`: on a laptop
   * narrow enough that 56rem does not fit, the floor gives way to the
   * container's own width instead of forcing a horizontal scrollbar. Above
   * that width, 70vw is free to grow past 56rem on anything wider still.
   */
  max-width: clamp(min(56rem, 100%), 70vw, 100%);
  margin-inline: auto;
}

/* The wide-display tier. A media query rather than a container query because
   the trigger is the *display class* (the same 2000px the title bar's
   `--bar-scale` keys on), not the pane's incidental width. */
@media (min-width: 2000px) {
  .store {
    --shop-scale: 1.25;
  }
}

/*
 * The rail. Sticky so the categories stay under the pointer while a long
 * shelf scrolls — the store is used mid-session, and hunting for the rail
 * after every scroll is the kind of friction a storefront exists to remove.
 */
.rail {
  position: sticky;
  top: 0.25rem;
  flex: none;
  width: 10rem;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.rail button {
  padding: 0.75rem 0.9rem;
  border: 1px solid var(--steel-700);
  border-left: 3px solid transparent;
  border-radius: 4px;
  background: var(--steel-800);
  color: var(--steel-400);
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 120ms ease, background 120ms ease, border-color 120ms ease;
}

.rail button:hover {
  color: var(--steel-200);
}

.rail button.active {
  border-left-color: var(--accent);
  background: var(--steel-700);
  color: #fff;
}

@media (prefers-reduced-motion: reduce) {
  .rail button {
    transition: none;
  }
}

.pane {
  flex: 1;
  min-width: 0;
  /*
   * The pane is a size container so the card grids can derive a track's width
   * from it (`100cqw` in `--card-track` above). Inline-size only: the pane's
   * width comes from the flex row, never from its contents, so the containment
   * costs nothing — and the height must stay free to grow with the shelves.
   *
   * `align-self: stretch` is the containment's seatbelt, and it was a shipped
   * fault first: the container's `align-items: flex-start` sizes a *column*
   * child (the compact layout) to its content width, and a size-contained
   * element's content width is nothing — the whole shop pane collapsed to a
   * ~30px ribbon on a phone, one word of the stub banner per line, caught by
   * `garage-landscape`. Stretch pins the cross axis to the container in both
   * directions; in the desktop row it only stands the pane as tall as the
   * flex line it already defines.
   */
  align-self: stretch;
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  /*
   * 1.75rem rather than 1.5 — a shelf boundary read as half a beat too short
   * once the cards themselves picked up a shadow and a hover lift of their own;
   * the extra quarter-rem is what keeps one shelf's glow from touching the next
   * shelf's heading. Compact halves it back down (below), where the budget is
   * height rather than calm.
   */
  gap: 1.75rem;
}

/*
 * The one thing on this pane that must never look like merchandise. It used to
 * read at the same weight as a card's own blurb text — bright strong, a solid
 * 0.78rem — which on a screen the owner calls the most important detail in the
 * game let a development disclaimer compete with the shelves for attention.
 * Smaller type, a duller rule and a duller `strong` let the eye settle past it
 * rather than on it, without hiding what it says.
 */
.stub-banner {
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-left: 2px solid #8a6a30;
  border-radius: 3px;
  background: rgba(216, 161, 58, 0.06);
  font-size: 0.68rem;
  line-height: 1.5;
  color: var(--steel-400);
}

.stub-banner strong {
  color: #cf9a52;
  font-weight: 600;
}

/*
 * The category heading.
 *
 * Raised from 0.7rem steel-400 — a whisper above a row of full-art cards, which
 * on a pane with three shelves left a player scanning for the boundary between
 * them. It now matches the Cosmetics tab's shelf label exactly (1.35rem, bold,
 * a hairline under it): that tab reuses this container, and a heading that was
 * twice the size on one side of the reuse than the other is the sort of drift
 * that makes two screens look like two products.
 */
h3 {
  margin: 0 0 1.1rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--steel-700);
  font-size: calc(1.35rem * var(--shop-scale));
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--steel-100, #eef1f4);
}

/*
 * The card grid is a **declared composition that fills the pane and scales
 * with it** — never a fixed pixel track floating in whatever room is left.
 *
 * It used to be `repeat(auto-fit, minmax(240px, 260px))` with
 * `justify-content: center`, measured against a 1440 Mac window — where it was
 * right, and everywhere wider it was wrong in the same direction: the cards
 * stayed 260px while the pane grew, so a Mac 16" showed a 109px dead band
 * either side of every shelf and a 27" monitor showed 400px, with the shelf
 * heading starting at the pane's edge and the cards starting somewhere else.
 * The owner's rule is the opposite: **no empty space inside the pane** (the
 * only sanctioned emptiness is the container's own 15% edge margins, which are
 * the `max-width: 70vw` cap above), and **bigger displays scale the same
 * composition up** rather than re-flowing it. So each shelf declares its
 * column count — a fact about the shelf, the Featured rule's own argument —
 * and the tracks are fractions, so a card's width is the pane's to give and
 * grows with the window: ~265px on a Mac 13", ~332px on a 16", ~516px on a
 * 27". The count matches the merchandise: three for the packs, the fuel tiers
 * and the bundles, four for the boost row (see `.quad`), two at hero scale.
 *
 * `--card-track` is one track's computed width, derived from the pane's own
 * measured size (`.pane` is a size container, so `100cqw` *is* the pane) —
 * never restated as a literal. It exists for its second reader: the
 * custom-amount strip's thumb is exactly one card's *picture* wide — the track
 * less the two borders the picture sits inside — and with the track derived,
 * the thumb and a pack's art box are the same box by construction at every
 * window width. `--cards-gap` is a property rather than a bare `gap` for the
 * same reason: the track arithmetic and the grid must read one figure.
 */
.cards {
  --cards-gap: 1rem;
  --card-cols: 3;
  --card-border: 1px;
  --card-track: calc(
    (100cqw - (var(--card-cols) - 1) * var(--cards-gap)) / var(--card-cols)
  );
  display: grid;
  gap: var(--cards-gap);
  align-items: stretch;
  grid-template-columns: repeat(var(--card-cols), minmax(0, 1fr));
}

/* The boost row: four boosts are served, so four tracks — the one count that
   puts the whole shelf on one full row with no hole at any width. A fifth
   boost shipping would bring partial rows back, which is this number's cue to
   be re-decided, not a bug. `.hero` (below, later in the cascade) still wins
   on Featured, where the weekly pair renders two-across. */
.cards.quad {
  --card-cols: 4;
}

/*
 * Small desktop: two across on the ordinary three-column shelves (Diamonds'
 * packs, Tanks' bundles), the same size the garage's own hulls and turrets
 * grid switches at (`.grid-mech` in `GarageScreen.vue`) — this pane and that
 * one are read at the same window sizes, so the two tiers move together.
 *
 * Bare `.cards`, not `.cards.quad` or `.cards.hero` — both are two classes
 * and already outrank this by specificity regardless of source order, so the
 * boost row's own four-across reasoning and Featured's own two-across
 * reasoning are both untouched here on purpose. Widening this to the boost
 * row is a real option if it ever reads cramped at this width, but it is a
 * separate decision with its own "no partial row" arithmetic to redo.
 */
@media (max-width: 68.75rem) {
  .cards {
    --card-cols: 2;
  }
}

/*
 * Featured: two columns, **declared** rather than derived — the limited-offer
 * bundle and pack must sit side by side in one row, which is the whole point
 * of merging their shelves.
 *
 * This was `repeat(auto-fit, minmax(340px, 1fr))`, which gives two tracks
 * only while two cards exist to fill them: `auto-fit` collapses the empty
 * repetition, so the week one of the two offers' windows outlived the
 * other's, the survivor's `1fr` track stretched to the whole pane — an
 * ~810px card on a 1440 window, which is the "huge lone card" a Mac
 * actually showed. Stating `repeat(2, …)` makes the row's shape a fact
 * about the shelf rather than about how many offers happen to be live;
 * the `:only-child` rule below is what a half-filled row looks like.
 * (The old comment's warning still stands one layer down: an auto-fit
 * count comes from the MAX track when it is definite, so never "fix" this
 * back to auto-fit with a bounded max — it counts one track and stacks
 * the pair.)
 */
.cards.hero,
.cards.wide.hero {
  --card-cols: 2;
  --cards-gap: 1.35rem;
}

/*
 * A shelf holding one card keeps the two-track geometry and centres the card
 * at a track-and-a-bit — featured, not inflated. Without this the lone card
 * sits left with a card-sized hole beside it, which reads as a missing item
 * rather than a curation.
 */
.cards.hero > .card:only-child {
  grid-column: 1 / -1;
  width: min(100%, calc(30rem * var(--shop-scale)));
  justify-self: center;
}

/* One column only when two genuinely cannot fit — a phone held sideways. */
@media (max-width: 44rem) {
  .cards.hero,
  .cards.wide.hero {
    --card-cols: 1;
  }
}

/* The custom-amount card: one wide card, centred, with room for a slider. */

/* The promotion hint on the experience strip. Not a colour of its own: it is the
   accent the rest of this shop uses for a figure worth noticing, at the note's
   own size, so it reads as part of the sentence rather than as a badge. */
.banner-note .promotes {
  color: var(--accent);
  font-style: normal;
}

/* The fuel shelf takes the base three tracks — one per tier, a full row at
   every width. The class stays because compact (end of file) has to name it:
   three cards split 2+1 is the one arrangement a three-rung ladder must not
   take, so it keeps its three columns even where everything else drops to two. */

.card {
  display: flex;
  flex-direction: column;
  border: var(--card-border) solid var(--steel-700);
  border-radius: 6px;
  background: var(--steel-800);
  overflow: hidden;
  /*
   * A card sat flush with the pane's own background before this — correct for a
   * document, flat for a shelf of things to buy. The shadow lifts it off the
   * backdrop; the extra lift on hover is the same cue a real shelf gives when a
   * hand reaches for something. Reduced motion drops the *transform* only — the
   * shadow is state (this card is a card), the transform is the one part of it
   * that moves.
   */
  box-shadow: 0 10px 22px -10px rgba(0, 0, 0, 0.55);
  transition: transform 140ms ease, box-shadow 140ms ease;
}

.card:hover {
  box-shadow: 0 16px 30px -10px rgba(0, 0, 0, 0.6);
  transform: translateY(-2px);
}

@media (prefers-reduced-motion: reduce) {
  .card {
    transition: none;
  }

  .card:hover {
    transform: none;
  }
}

.card.highlight,
.card.running {
  border-color: var(--accent);
}

/*
 * Hero scale: a card the Featured pane is actively pointing at, not merely
 * listing. Bigger type and a warm glow rather than a changed border colour —
 * a `deal` card keeps its amber border and a plain one keeps steel, so the
 * glow reads as "featured" independently of whichever other state the card
 * is already in.
 */
.card.hero {
  box-shadow:
    0 1.5rem 3rem rgba(0, 0, 0, 0.45),
    0 0 2.5rem rgba(232, 114, 44, 0.16);
}

/* The plain hover shadow above is equal specificity and later in the cascade
   than nothing here — stating hero's own hover keeps a featured card's glow
   the loudest thing on the shelf even while the pointer is on it. */
.card.hero:hover {
  box-shadow:
    0 2rem 3.6rem rgba(0, 0, 0, 0.5),
    0 0 3.2rem rgba(232, 114, 44, 0.24);
}

/* 1.7rem, down from 2 — at two declared columns a hero card is already half
   the pane, and 2rem type on top of that read as a poster on a Mac window. */
.card.hero .big {
  font-size: calc(1.7rem * var(--shop-scale));
}

.card.hero .eyebrow {
  font-size: calc(0.64rem * var(--shop-scale));
}

/* The Featured flag sits top-left, which is also where the label block starts —
   without this the flag prints straight over the eyebrow text. */
.card.hero .label {
  padding-top: 2.2rem;
}

.card.hero .art {
  aspect-ratio: 16 / 10;
}

.card.hero.bundle .art {
  aspect-ratio: 20 / 9;
}

/* A bundle already in the shed stays on the shelf and stays legible — it is
   still the answer to "what was in that one". It is only dimmed, never hidden:
   a card that vanished when you bought it would make the row re-lay itself,
   which is the bug the garage bays had. */
.card.bundle.complete {
  opacity: 0.62;
}

/*
 * The art region. Fixed aspect so a shelf of mixed offers still reads as one
 * shelf, dark radial plate underneath so a missing image is a quiet card
 * rather than a hole, and a scrim behind the label so the type reads over
 * whatever the picture puts in that corner.
 */
.art {
  position: relative;
  aspect-ratio: 16 / 9;
  background: radial-gradient(120% 120% at 30% 20%, var(--steel-600) 0%, var(--steel-800) 70%);
}

.art img,
.art .shot {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 0;
}

.art .label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  padding: 0.6rem 0.7rem;
  background: linear-gradient(135deg, rgba(11, 13, 17, 0.82) 0%, rgba(11, 13, 17, 0) 55%);
  pointer-events: none;
}

.eyebrow {
  font-size: calc(0.58rem * var(--shop-scale));
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--steel-200);
}

.big {
  margin: 0;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: calc(1.5rem * var(--shop-scale));
  line-height: 1.1;
  color: #fff;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.8);
}

.big.gem {
  color: #9fd7f5;
}

.big.name {
  font-family: inherit;
  font-size: calc(1.02rem * var(--shop-scale));
  letter-spacing: 0.03em;
}

.sub {
  font-style: normal;
  font-size: calc(0.66rem * var(--shop-scale));
  color: var(--steel-200);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
}

.sub.dim {
  color: var(--steel-400);
}

.flag {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  padding: 0.2rem 0.48rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: rgba(11, 13, 17, 0.72);
  color: var(--accent);
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
}

/* The one flag that lives in the *left* corner, so a deal's countdown and a
   hero's badge never have to fight over the same spot. */
.flag.featured {
  left: 0.55rem;
  right: auto;
  /*
   * Filled rather than outlined — the one flag that is a claim about the card
   * ("look here") rather than a fact about it ("owned", "ends in 6d"), so it is
   * the one flag drawn as a stamp instead of a label.
   */
  border-color: var(--accent);
  background: var(--accent);
  color: #14181f;
}

.flag.owned {
  border-color: var(--steel-600);
  color: var(--steel-400);
}

/* A live limited offer. Amber rather than the accent, so a deadline reads as
   its own kind of information beside "Best value" and "Owned". */
.card.deal {
  border-color: #d8a13a;
}

.flag.deal {
  border-color: #d8a13a;
  color: #e8bd6a;
}

.sub s {
  margin-right: 0.35rem;
  color: var(--steel-400);
}

/*
 * The discount, as a small filled chip rather than plain digits trailing the
 * struck price — legible as "this is a deal" from the corner of the eye,
 * without matching the plate's own accent. Matching it would put a −25% chip
 * in competition with the price for the loudest thing on the card, which the
 * headline-price rule reserves for the plate alone — so this stays amber, one
 * step down from the accent, reading as information rather than as a second
 * price.
 */
.pct {
  display: inline-block;
  padding: 0.03rem 0.32rem;
  border-radius: 2px;
  background: rgba(216, 161, 58, 0.18);
  color: #e8bd6a;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.flag.active {
  border-color: #7fc48c;
  color: #7fc48c;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.85rem 0.9rem;
}

.contents {
  margin: 0;
  font-size: calc(0.72rem * var(--shop-scale));
  letter-spacing: 0.04em;
  color: var(--steel-200);
}

.blurb {
  margin: 0;
  font-size: calc(0.74rem * var(--shop-scale));
  line-height: 1.45;
  color: var(--steel-400);
}

.part-owned {
  margin: 0;
  padding: 0.45rem 0.55rem;
  border-left: 2px solid #d8a13a;
  border-radius: 2px;
  background: rgba(216, 161, 58, 0.09);
  font-size: calc(0.7rem * var(--shop-scale));
  line-height: 1.45;
  color: var(--steel-200);
}

/* ─── The custom-amount row banner ──────────────────────────────────────── */

/*
 * A strip, not a card: the picture beside the control, then the plate across the
 * foot — the same edge, and the same full width, every card on this pane puts
 * its price on. The plate was a column down the right, which read as a band of
 * orange competing with the picture rather than as this shelf's buy button.
 *
 * It spans every track of the `.cards` grid it sits in, which is what makes its
 * width the pack row's width rather than the pane's. See the template.
 */
.banner {
  grid-column: 1 / -1;
  display: flex;
  /*
   * The thumb is **exactly one pack card's picture** — the card track less the
   * two borders that picture sits inside, and 16/9 like every `.art` on the
   * pane — so the strip reads as the last item on the same shelf rather than as
   * its own section with its own idea of how big a picture is. It was a 30%
   * share, which is a different size on every window that fits a different
   * number of cards, and cropped the art differently at each.
   *
   * **Flex-wrap rather than grid, and that is what removed a breakpoint.** The
   * strip's width is a whole number of card tracks, so at the narrowest pane it
   * *is* one card wide — where a fixed thumb is the entire strip and a `1fr`
   * body is squeezed to nothing. A media query cannot see that: the pane is the
   * viewport less the rail, so the one-track width is not a round viewport
   * figure, and the 46rem guess left a 737px window claiming room for a picture
   * and a control side by side (measured: the art took the strip and the body
   * overflowed out of it). With the thumb as an unshrinkable basis the body
   * wraps under it exactly when it no longer fits beside it, which is the same
   * condition stated as arithmetic the browser does rather than as a number
   * somebody has to keep true.
   */
  flex-wrap: wrap;
  align-items: stretch;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: var(--steel-800);
  overflow: hidden;
}

/*
 * 16/9, the same as `.art`, and that is what decides the strip's height: the
 * body is three short rows and never the taller of the two, so the row is the
 * picture's own height and the picture is never stretched or cropped. It also
 * means the thumb stays a whole uncropped picture at every width, which is why
 * there is no `object-position` here any more.
 */
.banner-art {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /*
   * `flex: 0 0` — it may not shrink, which is what makes the body wrap under it
   * instead of the two sharing a width too small for either. `max-width: 100%`
   * is the one concession: at a one-track strip the basis and the content box
   * are the same width (one card track less its borders), and a rounding hair
   * must not start a horizontal scrollbar.
   */
  flex: 0 0 calc(var(--card-track) - 2 * var(--card-border));
  max-width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(160deg, var(--steel-900), var(--steel-800));
  border-right: 1px solid var(--steel-700);
}

.banner-art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* The honest placeholder, when no art ships. Dim on purpose — it is a frame for
   the figure beside it, not a thing to look at. */
.banner-mark {
  font-size: 1.6rem;
  opacity: 0.35;
}

/*
 * `18rem` of basis is the width below which the figure line stops being a line
 * at all, so it is the point the body should wrap under the picture rather than
 * keep squeezing. It grows to whatever is left beside the thumb.
 *
 * The padding is two custom properties because the plate inside cancels it with
 * negative margins to sit flush against the body's edges — two literals for one
 * measurement is how a button ends up one pixel off an edge.
 */
.banner-body {
  --banner-pad-y: 0.9rem;
  --banner-pad-x: 1.1rem;
  flex: 1 1 18rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: var(--banner-pad-y) var(--banner-pad-x);
  min-width: 0;
}

/*
 * The strip's own type scale, raised as one pass.
 *
 * A card in the grid is one track wide (~265px on a Mac 13") and its type is
 * sized for that; this strip is the pack row's whole width — the full pane —
 * and inherited the card's sizes, so every line in it was set for a fraction
 * of the room it actually has. The rate, the note and the number box are all
 * stated here rather than on the shared classes, because those classes still
 * belong to the cards, where the small sizes are right.
 */
/*
 * The rate holds the figure line's right end, quiet against the two loud
 * figures beside it. `margin-left: auto` is what parks it there; the eyebrow
 * row it used to share ("Any amount" / "Rank up") is deleted, so this is the
 * rate's only home.
 */
.banner-rate {
  margin-left: auto;
  font-size: calc(0.8rem * var(--shop-scale));
  letter-spacing: 0.04em;
  color: var(--steel-400);
}

/*
 * The figure line leads the strip: the money and the count on one row, the
 * rate parked at its right end. The controls live on `.banner-track` below —
 * the offer first, then the lever that changes it.
 */
.banner-figure {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.8rem;
  min-width: 0;
}

/* The control row under the figure: the track, then the number box. */
.banner-track {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
}

/*
 * The money, and it is the loudest thing here on purpose.
 *
 * Same size as the count beside it and in the accent, because the plate no
 * longer carries a figure — see the template. A player glancing at this strip
 * should read what it costs before they read what they get.
 */
.banner-money {
  flex: none;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: calc(1.85rem * var(--shop-scale));
  line-height: 1.1;
  font-weight: 700;
  color: var(--accent);
}

.banner-count {
  flex: none;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: calc(1.85rem * var(--shop-scale));
  line-height: 1.1;
  color: #fff;
}

/*
 * **The line is reserved whether or not there is anything on it.**
 *
 * *Reaches the next rank* appears and disappears as the slider is dragged, and
 * it used to appear and disappear as an *element*: `v-if` on the paragraph took
 * the line and one of `.banner-body`'s 0.5rem gaps with it, so the whole strip
 * grew and shrank by about 30 px while somebody was dragging a control inside
 * it. Every widget below the note — the Buy plate — moved under the pointer at
 * the moment it mattered, and on a shelf where this strip shares a row the row's
 * height moved with it.
 *
 * So the paragraph is always in the flow and only its *contents* are
 * conditional, with a floor of exactly one line of its own type. The height is
 * derived from the two figures that set it (`0.85rem` at the shelf's own scale,
 * times the line height stated right below it) rather than a pixel constant,
 * because `--shop-scale` moves with the viewport and a fixed floor would be
 * short at the top end and slack at the bottom.
 *
 * `data-promotes` carries the state that used to be readable from the element
 * simply existing — an always-drawn paragraph cannot answer that by being
 * present, and "is this purchase a promotion" is worth keeping answerable.
 */
.banner-note {
  margin: 0;
  min-height: calc(0.85rem * var(--shop-scale) * 1.4);
  line-height: 1.4;
  font-size: calc(0.85rem * var(--shop-scale));
  color: var(--steel-400);
}

/* The number box is a control the player types into, so it is the one part of
   the strip that was actively small — 0.7rem label and a 5.5rem field. */
.banner-body .custom-number {
  font-size: calc(0.85rem * var(--shop-scale));
}

.banner-body .custom-number input {
  width: 7rem;
  padding: 0.42rem 0.55rem;
  font-size: calc(0.95rem * var(--shop-scale));
}

/* A track sized for the strip rather than for a card's narrow body. */
.banner-track .custom-slider {
  min-width: 4rem;
  height: 1.4rem;
}

/* The plate carries one word, so it can afford the strip's own scale. */
.banner-body > .plate {
  font-size: calc(0.95rem * var(--shop-scale));
}

/*
 * The plate: at the foot of the **body**, under the controls it pays for, flush
 * to the body's own edges. It was a column down the strip's right-hand side
 * (a band of orange competing with the picture rather than reading as a buy
 * button), then a bar across the whole foot (which ran under the picture, so it
 * belonged to the strip rather than to the thing it buys).
 *
 * `margin-top: auto` is inherited from the shared `.plate` rule rather than
 * overridden here — in a flex column that is exactly what pushes it to the foot,
 * which is why `.banner-body` no longer sets `justify-content: center`. The
 * other three margins cancel the body's padding, derived from it so the two
 * cannot drift.
 *
 * `min-height` rather than a fraction, because the strip's height is the
 * picture's: 16/9 on a Mac 13" track (~265px) is ~149px, so 2.4rem is about a
 * quarter of the strip, and it stays in that region as the track scales with
 * the pane, since the picture and the plate both move with `--card-track`.
 */
.banner-body > .plate {
  min-height: 2.4rem;
  margin-inline: calc(-1 * var(--banner-pad-x));
  margin-bottom: calc(-1 * var(--banner-pad-y));
}

/* ─── The slider and its number box, shared by both strips ───────────────── */

.custom-slider {
  flex: 1;
  accent-color: var(--accent);
}

.custom-number {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex: none;
  font-size: 0.7rem;
  color: var(--steel-400);
}

.custom-number input {
  width: 5.5rem;
  padding: 0.3rem 0.4rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: var(--steel-900);
  color: #fff;
  font-family: ui-monospace, 'SF Mono', monospace;
}

/*
 * The price plate. Full-width, at the card's bottom edge, and it is the buy
 * button — what an offer costs and where you click to pay are the same pixels,
 * which is the one convention every storefront a player has ever used agrees
 * on. The headline on it is always what leaves your pocket.
 */
.plate {
  margin-top: auto;
  padding: 0.75rem 0.6rem;
  border: 0;
  /*
   * A hairline sheen rather than the flat fill this was — same accent, so
   * nothing about *which pixels are the price* changes, only how much they
   * look like a button worth pressing rather than a coloured label.
   */
  background: linear-gradient(180deg, #f08a48 0%, var(--accent) 55%, #d9661f 100%);
  color: #12161c;
  font-weight: 700;
  font-size: calc(0.85rem * var(--shop-scale));
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: filter 120ms ease, transform 80ms ease;
}

.plate:hover:not(:disabled) {
  filter: brightness(1.08);
}

.plate:active:not(:disabled) {
  transform: translateY(1px);
}

.plate:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .plate {
    transition: none;
  }
}

/*
 * ─── A phone held sideways ────────────────────────────────────────────────────
 *
 * Gated on `.garage.compact` — `GarageScreen`'s single `matchMedia` answer, the
 * discipline `ItemCard.vue` and `ItemWorkbench.vue` already follow. Desktop and
 * tablet never see a declaration in this block.
 *
 * Measured before it at 667×375: the rail's 10rem column left the pane 447px of
 * a 627px content area, the rail's four buttons were 160×39, and every buy plate
 * was 36px tall. The rail turns horizontal across the top — navigation across,
 * cards below at full width — which is the same shape the Cosmetics tab takes,
 * whose compact block in `GarageScreen.vue` is the copy of this one.
 *
 * **Not sticky, and that is a reversal** (owner instruction, 2026-08-22): it
 * shipped stuck to the top for the desktop rule's own reason — the shelves are
 * long, and hunting for the rail after every scroll is friction — and on a
 * phone that trade is backwards: a stuck 50px row over ~300px of content height
 * is a sixth of the screen spent on navigation for the whole session, over the
 * one dimension a landscape phone has none of. `position: static` overrides the
 * desktop sticky, so the row scrolls away with the shelf; the solid backdrop
 * and the z-index went with it, because both existed *because* it was stuck
 * (the desktop column never has cards behind it — a stuck row did). The row
 * itself still scrolls sideways (`overflow-x`) if its buttons outgrow the
 * screen. `GarageScreen.vue` carries the same reversal for the Cosmetics rail.
 */
.garage.compact .store {
  flex-direction: column;
  gap: 0.6rem;
}

.garage.compact .rail {
  position: static;
  width: 100%;
  flex-direction: row;
  overflow-x: auto;
  gap: 0.3rem;
  padding: 0.3rem 0;
}

/* The active mark moves to the bottom edge: a button in a row reads along its
   bottom, and its left edge is a seam between neighbours — the geometry the
   title bar's own tabs already state. */
.garage.compact .rail button {
  flex: none;
  min-height: 44px;
  border-left-width: 1px;
  border-bottom: 3px solid transparent;
}

.garage.compact .rail button.active {
  border-left-color: var(--steel-700);
  border-bottom-color: var(--accent);
}

/* Sized for a desktop pane; on 300px of content height the heading and its
   margins cost a fifth of the screen before the first card. */
.garage.compact h3 {
  margin-bottom: 0.6rem;
  padding-bottom: 0.35rem;
  font-size: 1rem;
}

/*
 * The desktop rhythm (1.75rem between shelves, 1rem between cards, 0.85rem of
 * body padding) is calm at the cost of height, and height is the one thing a
 * phone on its side does not have. Tightened rather than removed — a shelf
 * boundary still reads as one, at a third of the desktop gap.
 */
.garage.compact .pane {
  gap: 0.9rem;
}

/* `COMPACT_QUERY` is a height, so a wide-but-short window can match both this
   and the ≥2000px tier — the phone pins the scale back to 1, exactly as the
   title bar's `--bar-scale` does. */
.garage.compact .store {
  --shop-scale: 1;
}

.garage.compact .cards {
  --cards-gap: 0.6rem;
}

/* Two tracks on a phone held sideways — the count the old auto-fit landed on
   at 667px, kept. `:not(.hero)` leaves the hero shelves to the 44rem media
   rule above, which takes them to one column on the same screens. */
.garage.compact .cards:not(.hero) {
  --card-cols: 2;
}

/* Except the fuel ladder, which must never split 2+1 — three ~210px cards
   share the row instead. */
.garage.compact .cards.fuel-tiers {
  --card-cols: 3;
}

.garage.compact .body {
  padding: 0.6rem 0.7rem;
  gap: 0.4rem;
}

/* The strips' own padding is a pair of custom properties for exactly this —
   the plate's negative margins are derived from them, so tightening the pair
   tightens the plate's own flush edges with it rather than leaving a gap. */
.garage.compact .banner-body {
  --banner-pad-y: 0.65rem;
  --banner-pad-x: 0.8rem;
}

/* A disclaimer costs proportionally more of a 375px-tall screen than of a
   900px one; it shrinks again here rather than only on the desktop pass. */
.garage.compact .stub-banner {
  padding: 0.4rem 0.6rem;
  font-size: 0.62rem;
  line-height: 1.4;
}

/* The lift reads as clutter at this height, and a touch screen mostly never
   triggers `:hover` in the first place — the shadow underneath still speaks
   for the card without the motion. */
.garage.compact .card:hover {
  transform: none;
}

/*
 * Every plate is the buy button, and every one measured 36px tall — under the
 * 44px floor `CLAUDE.md` sets, on the control that moves money. The custom
 * strips' plates inherit this through the shared class.
 */
.garage.compact .plate {
  min-height: 44px;
}

/* The boost effect line: "EXP ×2 for 24h", or a coin that slides open to read
   "credits" when the card is hovered — the coin alone is the legend most of the
   time and the word is there for whoever asks. */
.boost-effect {
  display: inline-flex;
  align-items: center;
  gap: 0.28em;
  font-size: calc(0.74rem * var(--shop-scale));
  color: #fff;
}

.boost-effect b {
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.coin-reveal {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
}

.coin-word {
  display: inline-block;
  max-width: 0;
  margin-left: 0;
  opacity: 0;
  white-space: nowrap;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.08em;
  transition:
    max-width 220ms ease,
    margin-left 220ms ease,
    opacity 160ms ease;
}

.card:hover .coin-word,
.card:focus-within .coin-word {
  max-width: 6em;
  margin-left: 0.3em;
  padding-right: 0.15em;
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .coin-word {
    transition: none;
  }
}

/* "N left" on a running boost sits at the foot of the picture, so a long card
   name is never printed underneath it. */
.flag.active {
  top: auto;
  bottom: 0.55rem;
}
</style>
